import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import api from '../services/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Traitement du paiement en cours...');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Token de paiement manquant');
      return;
    }

    // Appeler le backend pour capturer le paiement
    api.post(`/payments/capture/${token}`)
      .then((response) => {
        setStatus('success');
        if (response.data.status === 'awaiting_webhook') {
          setMessage('Paiement capturé ! Activation de votre abonnement en cours...');
          
          // 🔑 NOUVEAU: Démarrer un polling pour vérifier si le webhook a traité
          startPollingForActivation(token);
        } else if (response.data.status === 'completed') {
          setMessage('Paiement confirmé ! Votre abonnement est activé.');
        } else {
          setMessage('Paiement traité avec succès !');
        }
      })
      .catch((error) => {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Erreur lors de la validation du paiement');
      });
  }, [searchParams]);

  // Polling pour vérifier l'activation de l'abonnement
  const startPollingForActivation = (orderToken) => {
    let attempts = 0;
    const maxAttempts = 15; // 15 tentatives = 30 secondes max
    
    const pollInterval = setInterval(async () => {
      attempts++;
      
      try {
        // Vérifier si la transaction est completed
        const response = await api.get(`/client/dashboard`);
        
        // Si on reçoit des données, c'est que le webhook a traité
        if (response.data && response.data.subscriptions) {
          clearInterval(pollInterval);
          setMessage('✅ Abonnement activé ! Redirection vers votre dashboard...');
          setCountdown(2); // Réduire le countdown à 2s
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
      
      // Arrêter après maxAttempts
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        setMessage('Votre abonnement sera bientôt activé. Vous pouvez revenir au dashboard.');
      }
    }, 2000); // Vérifier toutes les 2 secondes
  };

  // Countdown et redirection automatique
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      // Utiliser window.location pour forcer un rechargement complet
      window.location.href = '/client/dashboard';
    }
  }, [status, countdown]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] px-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full text-center"
      >
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 text-[#00FFFF] animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Traitement en cours</h1>
            <p className="text-[#B8B8BA]">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#00FF88]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-[#00FF88]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Paiement réussi !</h1>
            <p className="text-[#B8B8BA] mb-6">{message}</p>
            
            <div className="bg-[#222226] p-4 rounded-lg mb-6">
              <p className="text-sm text-[#B8B8BA]">
                Redirection automatique dans <span className="text-[#00FFFF] font-bold">{countdown}s</span>
              </p>
            </div>

            <button 
              onClick={() => window.location.href = '/client/dashboard'}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Aller au dashboard
              <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertTriangle className="h-16 w-16 text-[#FF3B5C] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Erreur</h1>
            <p className="text-[#B8B8BA] mb-6">{message}</p>
            
            <button 
              onClick={() => window.location.href = '/client/dashboard'}
              className="btn-primary w-full"
            >
              Retour au dashboard
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
